import { useCallback, useEffect, useRef, useState } from 'react';
import { localeUI } from '../lib/format';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { encolarEnvio } from '../lib/envioConDeshacer';
import { useI18n } from '../lib/i18n';
import { renderLinks } from '../lib/linkify';

/**
 * ALERTA INSISTENTE DE SMS.
 *
 * Un SMS del homeowner sin contestar no es una notificación más: es alguien esperando. La
 * notificación normal se apila con las otras veinte y se pierde, así que esto escala a
 * propósito:
 *
 *   1ª y 2ª vez → banner arriba, se puede posponer.
 *   3ª vez      → la ventana COMPLETA en el medio de la pantalla, con el hilo y el cuadro de
 *                 respuesta. A esa altura ya pasaron 4 minutos sin contestar y el punto es que
 *                 responder sea más fácil que seguir ignorándolo.
 *
 * "Contestado" lo decide el BACKEND por el hecho real (hay un SMS saliente posterior a ese
 * número), no por un botón de "ok, lo vi": marcar como visto y no responder es exactamente el
 * comportamiento que esto viene a corregir.
 *
 * La cuenta de escalada vive en sessionStorage: si alguien cierra el banner y recarga la página
 * para escaparse, vuelve en el nivel donde estaba.
 */

type Pendiente = {
  id: string; claim_id: string | null; claim_name: string | null;
  phone: string | null; body: string; at: string; waiting_minutes: number;
};

type HiloMsg = { id: string; direction: string; body: string; created_at: string; author: string | null; phone: string | null };
const ultimos10 = (p: string | null | undefined) => (p ?? '').replace(/\D/g, '').slice(-10);

/** Cada cuánto vuelve a aparecer si sigue sin respuesta. */
const REAPARECE_MS = 2 * 60_000;
/** A partir de esta escalada ya no es banner, es la ventana completa. */
const NIVEL_VENTANA = 3;

const KEY = 'sms:escalada';
const leerEscalada = (): Record<string, number> => {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}') as Record<string, number>; } catch { return {}; }
};
const guardarEscalada = (m: Record<string, number>) => {
  try { sessionStorage.setItem(KEY, JSON.stringify(m)); } catch { /* incógnito */ }
};

export function SmsAlert() {
  const { t } = useI18n();
  const [pend, setPend] = useState<Pendiente[]>([]);
  const [oculto, setOculto] = useState<Record<string, number>>({}); // id → cuándo volver a mostrar
  const [nivel, setNivel] = useState<Record<string, number>>(leerEscalada);
  const [respuesta, setRespuesta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cargando = useRef(false);
  // ABRIR LA CONVERSACIÓN DESDE EL BANNER (pedido del dueño 2026-09-12: "darle click y que se abra en
  // el medio toda la conversación para poder responderle"). Antes la ventana solo aparecía en la
  // tercera insistencia y mostraba UN mensaje; ahora un clic la abre con el hilo completo.
  const [abierto, setAbierto] = useState<string | null>(null);
  const [hilo, setHilo] = useState<HiloMsg[] | null>(null);
  const finHilo = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (cargando.current) return;
    cargando.current = true;
    try {
      const r = await api.get<{ pending: Pendiente[] }>('/api/sms/pending');
      setPend(r.pending ?? []);
    } catch { /* silencioso: una alerta que rompe la app es peor que una alerta que falta */ }
    finally { cargando.current = false; }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => { if (!document.hidden) void load(); }, 30_000);
    const onVis = () => { if (!document.hidden) void load(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('sms:changed', load);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('sms:changed', load);
    };
  }, [load]);

  // Limpia la escalada de los que ya se contestaron: si no, el contador de un número viejo
  // reaparecería en el nivel 3 la próxima vez que escriban.
  useEffect(() => {
    const vivos = new Set(pend.map((p) => p.id));
    const m = leerEscalada();
    let cambio = false;
    for (const k of Object.keys(m)) if (!vivos.has(k)) { delete m[k]; cambio = true; }
    if (cambio) { guardarEscalada(m); setNivel(m); }
  }, [pend]);

  const ahora = Date.now();
  const visibles = pend.filter((p) => (oculto[p.id] ?? 0) <= ahora);
  const actual = visibles[0] ?? null;

  /** Posponer sube el nivel: la próxima vez insiste más. */
  function posponer(p: Pendiente) {
    setOculto((m) => ({ ...m, [p.id]: Date.now() + REAPARECE_MS }));
    const m = { ...leerEscalada(), [p.id]: (nivel[p.id] ?? 1) + 1 };
    guardarEscalada(m); setNivel(m);
  }

  async function responder(p: Pendiente) {
    if (!respuesta.trim() || !p.phone) return;
    setErr(null);
    const foto = { phone: p.phone, body: respuesta.trim(), texto: respuesta, id: p.id };
    setRespuesta(''); setAbierto(null);
    encolarEnvio({
      etiqueta: `${t('undo.smsTo')} ${foto.phone}`,
      ejecutar: (opts) => api.post('/api/sms/reply', { phone: foto.phone, body: foto.body }, opts).then(() => undefined),
      deshacer: () => { setRespuesta(foto.texto); setAbierto(foto.id); },
      alTerminar: (ok) => { if (ok) { window.dispatchEvent(new Event('sms:changed')); void load(); } },
    });
  }

  /** ASIGNAR A ALGUIEN (Manuel 2026-10-02): el hilo pasa a esa persona (la alerta se le va a ella)
   *  y le queda una tarea en su To Do. La lista de gente se pide al abrir la ventana. */
  const [miembros, setMiembros] = useState<Array<{ id: string; name: string }>>([]);
  const [asignando, setAsignando] = useState(false);
  useEffect(() => {
    if (!abierto || miembros.length) return;
    api.get<{ members: Array<{ id: string; name: string }> }>('/api/team/members').then((r) => setMiembros(r.members ?? [])).catch(() => {});
  }, [abierto, miembros.length]);
  async function asignar(p: Pendiente, userId: string) {
    if (!userId) return;
    setAsignando(true); setErr(null);
    try {
      await api.post(`/api/sms/${p.id}/assign-user`, { userId });
      setAbierto(null);
      window.dispatchEvent(new Event('sms:changed'));
      await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setAsignando(false); }
  }

  /** "No reply needed": hay SMS que no admiten respuesta (el sistema no los acepta) y la gente
   *  se aguantaba la alerta hasta que caducara. Esto la apaga PARA TODOS y deja la huella de
   *  quién lo decidió en el hilo del SMS Inbox (pedido 2026-08-10). */
  async function sinRespuesta(p: Pendiente) {
    setEnviando(true); setErr(null);
    try {
      await api.post(`/api/sms/${p.id}/no-reply-needed`, {});
      window.dispatchEvent(new Event('sms:changed'));
      await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setEnviando(false); }
  }

  // El hilo completo del número, del más viejo al más nuevo, cuando la ventana está abierta.
  const telefono = actual?.phone ?? null;
  const ventana = !!actual && ((nivel[actual.id] ?? 1) >= NIVEL_VENTANA || abierto === actual.id);
  useEffect(() => {
    if (!ventana || !telefono) { setHilo(null); return; }
    const p10 = ultimos10(telefono);
    let vivo = true;
    // Sin `q`: el filtro del inbox busca el texto tal cual y los números se guardan con formato
    // ("(404) 421-9484"), así que los 10 dígitos no matchean. Se filtra acá por los últimos 10.
    api.get<{ messages: HiloMsg[] }>('/api/sms/inbox')
      .then((r) => {
        if (!vivo) return;
        const msgs = (r.messages ?? []).filter((m) => ultimos10(m.phone) === p10)
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        setHilo(msgs);
        // Leído: abrirlo es leerlo (los entrantes del hilo).
        const entrantes = msgs.filter((m) => m.direction === 'in').map((m) => m.id);
        if (entrantes.length) api.post('/api/sms/mark-read', { ids: entrantes }).catch(() => {});
        window.setTimeout(() => finHilo.current?.scrollIntoView({ block: 'end' }), 30);
      })
      .catch(() => { if (vivo) setHilo([]); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventana, telefono]);

  if (!actual) return null;
  const n = nivel[actual.id] ?? 1;
  const quien = actual.claim_name || actual.phone || t('smsAlert.someone');

  // ─── la ventana en el medio: por insistencia (nivel 3+) o porque le dieron clic al banner ───
  if (ventana) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', padding: 16 }}>
        <div className="card" style={{ width: 'min(560px, 100%)', margin: 0, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-flex' }}><MessageSquare size={20} strokeWidth={1.75} /></span>
            <b style={{ fontSize: 'var(--fs-base)' }}>{t('smsAlert.titleModal').replace('{n}', quien)}</b>
            <span style={{ flex: 1 }} />
            <span className="badge" style={{ background: 'var(--bad-fill)', color: 'var(--bad-fill-ink)', fontSize: 'var(--fs-2xs)' }}>
              {t('smsAlert.waiting').replace('{m}', String(actual.waiting_minutes))}
            </span>
          </div>

          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: 10, maxHeight: '42vh', overflowY: 'auto', display: 'grid', gap: 6 }}>
            {hilo === null ? (
              <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('common.loading')}</div>
            ) : hilo.length === 0 ? (
              <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap' }}>{renderLinks(actual.body)}</div>
            ) : hilo.map((m) => {
              const mio = m.direction === 'out';
              return (
                <div key={m.id} style={{ display: 'grid', justifyItems: mio ? 'end' : 'start' }}>
                  <div style={{ maxWidth: '85%', padding: '7px 10px', borderRadius: 12, fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                    background: mio ? 'var(--accent)' : 'var(--card)', color: mio ? '#fff' : 'var(--ink)', border: mio ? 'none' : '1px solid var(--line)' }}>
                    {renderLinks(m.body, mio)}
                  </div>
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>
                    {mio && m.author ? `${m.author} · ` : ''}{new Date(m.created_at).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
              );
            })}
            <div ref={finHilo} />
          </div>

          <textarea className="input" autoFocus style={{ minHeight: 90, fontSize: 'var(--fs-sm)' }}
            placeholder={t('smsAlert.replyPh')} value={respuesta} onChange={(e) => setRespuesta(e.target.value)} />
          {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/sms" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }}>{t('smsAlert.openInbox')} →</Link>
            {actual.claim_id && <Link to={`/claims/${actual.claim_id}`} className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }}>{t('smsAlert.openClaim')}</Link>}
            <span style={{ flex: 1 }} />
            {/* No hay "cerrar" a secas: se pospone, y posponer vuelve a insistir. */}
            <select className="input sm" disabled={asignando} value="" onChange={(e) => { void asignar(actual, e.target.value); }} title={t('smsAlert.assignTitle')} style={{ maxWidth: 190 }}>
              <option value="">{t('smsAlert.assignTo')}</option>
              {miembros.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button className="btn ghost sm" disabled={enviando} onClick={() => { void sinRespuesta(actual); }}>{t('smsAlert.noReply')}</button>
            {abierto === actual.id && n < NIVEL_VENTANA
              ? <button className="btn ghost sm" onClick={() => setAbierto(null)}>{t('common.close')}</button>
              : <button className="btn ghost sm" onClick={() => posponer(actual)}>{t('smsAlert.snooze')}</button>}
            <button className="btn sm" style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }}
              disabled={enviando || !respuesta.trim()} onClick={() => { void responder(actual); }}>
              {enviando ? '…' : t('smsAlert.send')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── nivel 1-2: banner ───
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 300,
      background: 'var(--bad-fill)', color: 'var(--bad-fill-ink)',
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '8px 14px', fontSize: 'var(--fs-sm)',
    }}>
      <span style={{ display: 'inline-flex' }}><MessageSquare size={16} strokeWidth={1.75} /></span>
      {/* El nombre y el mensaje abren la conversación entera en el medio, con el cuadro de respuesta. */}
      <button type="button" onClick={() => setAbierto(actual.id)} title={t('smsAlert.openThread')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, background: 'none', border: 'none', color: '#fff', font: 'inherit', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
        <b>{t('smsAlert.titleBanner').replace('{n}', quien)}</b>
        <span style={{ opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 }}>
          «{actual.body}»
        </span>
      </button>
      <span style={{ opacity: 0.85, fontSize: 'var(--fs-2xs)' }}>{t('smsAlert.waiting').replace('{m}', String(actual.waiting_minutes))}</span>
      <span style={{ flex: 1 }} />
      <button type="button" onClick={() => setAbierto(actual.id)}
        style={{ background: 'none', border: 'none', color: '#fff', fontWeight: 'var(--fw-bold)', textDecoration: 'underline', fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: 0 }}>
        {t('smsAlert.reply')}
      </button>
      <button onClick={() => { void sinRespuesta(actual); }}
        style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 9px', cursor: 'pointer', fontSize: 'var(--fs-2xs)' }}>
        {t('smsAlert.noReply')}
      </button>
      <button onClick={() => posponer(actual)}
        style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 9px', cursor: 'pointer', fontSize: 'var(--fs-2xs)' }}>
        {t('smsAlert.snooze')}
      </button>
    </div>
  );
}
